<script setup lang="ts">
import { computed } from "vue";
import { Button } from "@/components/ui/button";
import { useI18n } from "vue-i18n";

interface Props {
    bookingUrl: string;
    eventName: string;
}

const props = defineProps<Props>();

const { t } = useI18n();

const shareText = computed(() => {
    return t("event_types.share_hub.social.share_text", {
        name: props.eventName,
    });
});

const twitterUrl = computed(() => {
    const text = encodeURIComponent(shareText.value);
    const url = encodeURIComponent(props.bookingUrl);
    return `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
});

const linkedinUrl = computed(() => {
    const url = encodeURIComponent(props.bookingUrl);
    return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
});

const facebookUrl = computed(() => {
    const url = encodeURIComponent(props.bookingUrl);
    return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
});

const emailUrl = computed(() => {
    const subject = encodeURIComponent(
        t("event_types.share_hub.social.email_subject", {
            name: props.eventName,
        }),
    );
    const body = encodeURIComponent(
        `${shareText.value}\n\n${props.bookingUrl}`,
    );
    return `mailto:?subject=${subject}&body=${body}`;
});

function openShare(url: string) {
    window.open(url, "_blank", "width=600,height=400");
}

function openEmail() {
    window.location.href = emailUrl.value;
}
</script>

<template>
    <div class="space-y-4">
        <p class="text-sm text-iron-600">
            {{ $t("event_types.share_hub.social.description") }}
        </p>

        <div class="gap-3 grid grid-cols-2">
            <!-- Twitter/X -->
            <Button
                variant="outline"
                @click="openShare(twitterUrl)"
                class="gap-2 py-3 h-auto justify-start"
            >
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path
                        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                    />
                </svg>
                <span>Twitter / X</span>
            </Button>

            <!-- LinkedIn -->
            <Button
                variant="outline"
                @click="openShare(linkedinUrl)"
                class="gap-2 py-3 h-auto justify-start"
            >
                <svg
                    class="h-5 w-5 text-[#0A66C2]"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                >
                    <path
                        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                    />
                </svg>
                <span>LinkedIn</span>
            </Button>

            <!-- Facebook -->
            <Button
                variant="outline"
                @click="openShare(facebookUrl)"
                class="gap-2 py-3 h-auto justify-start"
            >
                <svg
                    class="h-5 w-5 text-[#1877F2]"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                >
                    <path
                        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                    />
                </svg>
                <span>Facebook</span>
            </Button>

            <!-- Email -->
            <Button
                variant="outline"
                @click="openEmail"
                class="gap-2 py-3 h-auto justify-start"
            >
                <svg
                    class="h-5 w-5 text-iron-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                <span>{{ $t("event_types.share_hub.social.email") }}</span>
            </Button>
        </div>
    </div>
</template>
